/**
 * @description 埋点追踪模块
 * @example 使用方式：
 * 1. 指令方式：
 *    <button v-tracker="{ event: 'button_click', data: { buttonId: 'submit-btn', category: 'form' }}">点击追踪</button>
 *    <button v-tracker>点击追踪</button>
 *    <button v-tracker="'button_click'">点击追踪</button>
 * 2. Hook方式：
 *    - 页面访问追踪：usePageTracker(route)
 *    - API方式追踪：
 *      const { track } = useTracker()
 *      track('custom_event', { customData: 'value' })
 */

import { trackingPoint } from '@/apis/burialApi'
import type { RoleMenuType } from '@/types/menu'
import { useResource } from '@/hooks/useResource'
import dayjs from 'dayjs'

/**
 * 浏览器及设备信息接口
 */
interface BrowserInfo {
  browser: string // 浏览器名称
  browserVersion: string // 浏览器版本
  os: string // 操作系统
  osVersion: string // 操作系统版本
  device: string // 设备类型：mobile/tablet/desktop
  deviceModel: string // 设备型号
  userAgent: string // 完整的 UserAgent 字符串
}

type ActionType = 'CLICK' | 'EXPORT' | 'IMPORT' | 'BROWSE'
type ResultStatus = 'SUCCESS' | 'FAIL'

/**
 * 埋点数据接口
 */
interface TrackerInfo {
  action: string // 行为 CLICK(点击),EXPORT(导出),IMPORT(导入),BROWSE(浏览),可用值:BROWSE,CLICK,EXPORT,IMPORT
  appCode: string // 页面code（paas应用code)
  endTime?: string // 行为结束时间 持续性数据才传
  ext?: any // 扩展数据
  reportTime?: string // 上报时间为空
  resultStatus?: string // 行为结果 SUCCESS,FAIL,可用值:FAIL,SUCCESS
  startTime?: string // 行为开始时间 持续性数据才传
  systemCode: string // 子系统code
  tablePageFlag?: boolean // 是否表单页
  path?: string // 页面路径
}

/**
 * 埋点追踪类（单例模式）
 */
class Tracker {
  private static instance: Tracker // 单例实例
  private startTime: number = 0 // 页面访问开始时间
  public leaveRouteInfo: RoleMenuType | null = null // 当前路由信息
  private sleepStartTime: number = 0 // 网站未激活开始时间

  /**
   * 获取浏览器和设备信息
   * @returns BrowserInfo 浏览器和设备信息对象
   */
  public getBrowserInfo(): BrowserInfo {
    const ua = navigator.userAgent
    const browserRegexes = {
      Chrome: /Chrome\/([0-9.]+)/,
      Firefox: /Firefox\/([0-9.]+)/,
      Safari: /Version\/([0-9.]+).*Safari/,
      WeChat: /MicroMessenger\/([0-9.]+)/
    }

    const osRegexes = {
      Android: /Android ([0-9.]+)/,
      iOS: /OS ([0-9_]+) like Mac OS X/,
      Windows: /Windows NT ([0-9.]+)/,
      HarmonyOS:
        /(?:HarmonyOS|HMSCore|HMOS|ArkWeb) ([0-9.]+)|; ?([^;)]+) ?(?:Build\/HMOS|HARMONYOS|ArkWeb)/
    }

    let browser = 'unknown'
    let browserVersion = 'unknown'
    let os = 'unknown'
    let osVersion = 'unknown'
    let device = 'unknown'

    // 检测浏览器和版本
    for (const [name, regex] of Object.entries(browserRegexes)) {
      const match = ua.match(regex)
      if (match) {
        browser = name
        browserVersion = match[1]
        break
      }
    }

    // 检测操作系统和版本
    for (const [name, regex] of Object.entries(osRegexes)) {
      const match = ua.match(regex)
      if (match) {
        os = name
        if (name === 'HarmonyOS') {
          osVersion = match[1] || match[2] || 'unknown'
        } else {
          osVersion = match[1].replace(/_/g, '.')
        }
        break
      }
    }

    // 检测设备类型
    if (/Mobile|iPhone|iPad|Android/.test(ua)) {
      device = 'mobile'
    } else if (/Tablet|iPad/.test(ua)) {
      device = 'tablet'
    } else {
      device = 'desktop'
    }

    // 获取设备型号
    let deviceModel = 'unknown'
    const mobileModel = ua.match(/\(([^)]+)\)/)
    if (mobileModel && mobileModel[1]) {
      // 提取设备型号信息
      if (os === 'iOS') {
        // iOS设备: iPhone、iPad等
        const iosMatch = ua.match(/\b(iPad|iPhone|iPod)\b.*OS\s([\d_]+)/)
        deviceModel = iosMatch ? iosMatch[1] : 'iOS Device'
      } else if (os === 'Android') {
        // Android设备
        const androidMatch = ua.match(/;\s([^;]+)\sbuild/i) || ua.match(/\b([^;)]+)\);\swv\)/i)
        deviceModel = androidMatch ? androidMatch[1].trim() : 'Android Device'
      }
    }

    return {
      browser,
      browserVersion,
      os,
      osVersion,
      device,
      deviceModel,
      userAgent: ua
    }
  }

  /**
   * 私有构造函数，初始化基础信息
   */
  private constructor() {}

  /**
   * 获取单例实例
   * @returns Tracker 追踪器实例
   */
  public static getInstance(): Tracker {
    if (!Tracker.instance) {
      Tracker.instance = new Tracker()
    }
    return Tracker.instance
  }

  /**
   * 发送埋点数据
   * @param data TrackerInfo 埋点数据
   */
  private async send(data: TrackerInfo) {
    try {
      /**
        action  行为 CLICK(点击),EXPORT(导出),IMPORT(导入),BROWSE(浏览),可用值:BROWSE,CLICK,EXPORT,IMPORT
        appCode 页面code（paas应用code)
        endTime  行为结束时间 持续性数据才传
        ext 	扩展数据
        reportTime 上报时间为空
        resultStatus  行为结果 SUCCESS,FAIL,可用值:FAIL,SUCCESS	
        startTime 行为开始时间 持续性数据才传
        systemCode  子系统code
        tablePageFlag 是否表单页
       */

      trackingPoint({
        ...data
      })
      // console.log('埋点数据：', data)
    } catch (error) {
      console.error('埋点上报失败：', error)
    }
  }

  /**
   * 记录页面访问
   * @param route 路由信息
   * @param extraData 额外数据
   */
  public trackPageView(route: RoleMenuType, extraData?: any) {
    if (this.leaveRouteInfo?.path === route.path) return
    this.startTime = Date.now()
    this.leaveRouteInfo = route
    const data: TrackerInfo = {
      action: 'CLICK',
      appCode: route.appCode,
      ext: extraData,
      reportTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      systemCode: route.systemCode || '',
      tablePageFlag: !route.noTable,
      path: route.path || ''
    }
    this.send(data)
  }

  /**
   * 记录页面离开  浏览未超过3S不上报，未有效浏览且超过1H不上报
   * @param route 路由信息
   * @param extraData 额外数据
   */
  public trackPageLeave(route: RoleMenuType, extraData?: any) {
    this.leaveRouteInfo = null
    const endTime = Date.now()
    if (endTime - this.startTime < 3000) return
    const data: TrackerInfo = {
      action: 'BROWSE',
      appCode: route.appCode,
      ext: extraData,
      startTime: dayjs(this.startTime).format('YYYY-MM-DD HH:mm:ss'),
      endTime: dayjs(endTime).format('YYYY-MM-DD HH:mm:ss'),
      reportTime: dayjs(endTime).format('YYYY-MM-DD HH:mm:ss'),
      systemCode: route.systemCode || '',
      tablePageFlag: !route.noTable || true,
      path: route.path || ''
    }
    this.send(data)
  }

  /**
   * 自定义事件追踪
   * @param route 路由信息
   * @param action 事件名称
   * @param resultStatus 事件结果
   * @param extraData 额外数据
   */
  public trackEvent(
    route: RoleMenuType,
    action: string,
    resultStatus: ResultStatus,
    extraData?: any
  ) {
    const data: TrackerInfo = {
      action,
      appCode: route.appCode,
      ext: extraData,
      reportTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      resultStatus,
      systemCode: route.systemCode || ''
    }
    this.send(data)
  }

  public trackExport(
    action: ActionType,
    resultStatus: ResultStatus,
    menuCode: string,
    extraData?: any
  ) {
    const { getCurrentMenu, getMenuInfoByCode } = useResource()
    let menu = getCurrentMenu()
    if (menuCode) {
      menu = getMenuInfoByCode(menuCode)?.[0] || menu
    }
    this.send({
      action: action,
      appCode: menu.appCode,
      ext: extraData,
      reportTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      resultStatus,
      systemCode: menu.systemCode || ''
    })
  }

  public clearLeaveRouteInfo() {
    this.leaveRouteInfo = null
  }

  public sleepStart() {
    this.sleepStartTime = Date.now()
  }

  public sleepEnd() {
    if (!this.sleepStartTime) return
    const sleepTime = Date.now() - this.sleepStartTime
    console.log('结束睡眠', sleepTime)
    // 如果大于一小时不上报
    if (sleepTime > 60 * 60 * 1000) {
      this.leaveRouteInfo = null
    }
  }
}

/**
 * 追踪器Hook
 * @returns { track } 返回track方法用于手动触发事件追踪
 */
export default function useTracker() {
  const tracker = Tracker.getInstance()

  const track = (route: RoleMenuType, action: string, resultStatus: ResultStatus, data?: any) => {
    tracker.trackEvent(route, action, resultStatus, data)
  }

  return {
    track,
    tracker
  }
}
